import React, { useState } from 'react';
import {
  Container,
  Box,
  Typography,
  Card,
  Grid,
  TextField,
  Button,
  Alert,
} from '@mui/material';
import { motion } from 'framer-motion';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import { useScrollAnimation, scaleIn } from '../hooks/useScrollAnimation';

// Passen Sie die Basis-URL an Ihr Backend an:

const ContactSection = () => {
  const [cardRef, cardControls] = useScrollAnimation();

  // 'form'  = Formular ausfüllen
  // 'code'  = Code eingeben
  // 'done'  = fertig
  const [step, setStep] = useState('form');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [token, setToken] = useState('');
  const [code, setCode] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    website: '', // Honeypot – bleibt leer
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Schritt 1: Formular absenden -> Code anfordern
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const api = "/api/contact"
      const res = await fetch(api, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setToken(data.token);
        setStep('code');
      } else {
        setError(data.message || 'Etwas ist schiefgelaufen. Bitte erneut versuchen.');
      }
    } catch (err) {
      setError('Verbindung fehlgeschlagen. Bitte später erneut versuchen.');
    } finally {
      setLoading(false);
    }
  };

  // Schritt 2: Code prüfen
  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const api = "/api/contact/verify"
      const res = await fetch(api, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, code }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setStep('done');
        setFormData({ name: '', email: '', subject: '', message: '', website: '' });
        setCode('');
      } else {
        setError(data.message || 'Falscher Code. Bitte prüfen Sie Ihre E-Mail.');
      }
    } catch (err) {
      setError('Verbindung fehlgeschlagen. Bitte später erneut versuchen.');
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    { icon: <EmailIcon />, label: 'Email', value: 'contact@km-bizz.com' },
    { icon: <PhoneIcon />, label: 'Phone', value: '+49 (176) 47696058' },
    { icon: <LocationOnIcon />, label: 'Location', value: 'Remote / Worldwide' },
  ];

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.1)' },
      '&:hover fieldset': { borderColor: 'primary.main' },
    },
  };

  return (
    <Box id="contact" sx={{ py: { xs: 10, md: 15 }, background: 'rgba(0, 0, 0, 0.2)' }}>
      <Container maxWidth="lg">
        <Card
          component={motion.div}
          ref={cardRef}
          initial="hidden"
          animate={cardControls}
          variants={scaleIn}
          sx={{
            background: 'var(--card-bg)',
            backdropFilter: 'blur(10px)',
            border: '1px solid var(--border-subtle)',
            p: { xs: 4, md: 8 },
          }}
        >
          <Grid container spacing={6}>
            {/* Linke Spalte */}
            <Grid item xs={12} md={5}>
              <Typography variant="h3" sx={{ mb: 3, fontWeight: 700 }}>
                Let's Work Together
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', mb: 4, lineHeight: 1.8 }}>
                Haben Sie ein Projekt im Kopf? Zum Schutz vor Spam bestätigen Sie Ihre
                E-Mail-Adresse mit einem kurzen Code – so kommt Ihre Nachricht garantiert an.
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {contactInfo.map((info, idx) => (
                  <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        width: 50, height: 50, borderRadius: 2,
                        background: 'rgba(0, 245, 255, 0.1)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        '& svg': { color: '#00F5FF' },
                      }}
                    >
                      {info.icon}
                    </Box>
                    <Box>
                      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                        {info.label}
                      </Typography>
                      <Typography variant="body1">{info.value}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Grid>

            {/* Rechte Spalte */}
            <Grid item xs={12} md={7}>
              {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                  {error}
                </Alert>
              )}

              {/* Schritt: Formular */}
              {step === 'form' && (
                <Box component="form" onSubmit={handleSubmit}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Ihr Name" name="name" required
                        value={formData.name} onChange={handleChange} sx={inputSx} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Ihre E-Mail" name="email" type="email" required
                        value={formData.email} onChange={handleChange} sx={inputSx} />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth label="Betreff" name="subject" required
                        value={formData.subject} onChange={handleChange} sx={inputSx} />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth label="Nachricht" name="message" required
                        multiline rows={5}
                        value={formData.message} onChange={handleChange} sx={inputSx} />
                    </Grid>

                    {/* Honeypot: für Menschen unsichtbar, nur Bots füllen es aus */}
                    <input
                      type="text"
                      name="website"
                      value={formData.website}
                      onChange={handleChange}
                      tabIndex="-1"
                      autoComplete="off"
                      aria-hidden="true"
                      style={{
                        position: 'absolute',
                        left: '-9999px',
                        width: '1px',
                        height: '1px',
                        opacity: 0,
                      }}
                    />

                    <Grid item xs={12}>
                      <Button type="submit" variant="contained" size="large" fullWidth
                        disabled={loading}
                        sx={{
                          py: 2,
                          background: 'linear-gradient(135deg, #00F5FF, #FF6B35)',
                          fontWeight: 700, fontSize: '1rem',
                          '&:hover': { background: 'linear-gradient(135deg, #00D9E6, #FF5722)' },
                        }}
                      >
                        {loading ? 'Wird gesendet...' : 'Code anfordern'}
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* Schritt: Code eingeben */}
              {step === 'code' && (
                <Box component="form" onSubmit={handleVerify}>
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
                    Code eingeben
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                    Wir haben einen 6-stelligen Code an <strong>{formData.email}</strong> gesendet.
                    Bitte prüfen Sie auch den Spam-Ordner.
                  </Typography>

                  <TextField fullWidth label="6-stelliger Code" required
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    inputProps={{
                      inputMode: 'numeric',
                      maxLength: 6,
                      style: { letterSpacing: '10px', fontSize: '1.5rem', textAlign: 'center' },
                    }}
                    sx={{ ...inputSx, mb: 3 }} />

                  <Button type="submit" variant="contained" size="large" fullWidth
                    disabled={loading || code.length !== 6}
                    sx={{
                      py: 2,
                      background: 'linear-gradient(135deg, #00F5FF, #FF6B35)',
                      fontWeight: 700, fontSize: '1rem', mb: 2,
                      '&:hover': { background: 'linear-gradient(135deg, #00D9E6, #FF5722)' },
                    }}
                  >
                    {loading ? 'Wird geprüft...' : 'Bestätigen & Senden'}
                  </Button>

                  <Button fullWidth variant="text"
                    onClick={() => { setStep('form'); setError(''); setCode(''); }}
                    sx={{ color: 'text.secondary' }}
                  >
                    Zurück / andere E-Mail verwenden
                  </Button>
                </Box>
              )}

              {/* Schritt: Fertig */}
              {step === 'done' && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <MarkEmailReadIcon sx={{ fontSize: 72, color: '#48BB78', mb: 2 }} />
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                    Nachricht gesendet!
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3 }}>
                    Vielen Dank – ich melde mich in Kürze bei Ihnen.
                  </Typography>
                  <Button variant="outlined"
                    onClick={() => setStep('form')}
                    sx={{ borderColor: 'primary.main', color: 'primary.main' }}
                  >
                    Weitere Nachricht senden
                  </Button>
                </Box>
              )}
            </Grid>
          </Grid>
        </Card>
      </Container>
    </Box>
  );
};

export default ContactSection;
